import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';

export default function PaymentStaff() {
  const [bookingData, setBookingData] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedPaymentMethodID, setSelectedPaymentMethodID] = useState(null);
  const [boothData, setBoothData] = useState(null);
  const [token, setToken] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const storedData = sessionStorage.getItem('bookingData');
    const storedToken = sessionStorage.getItem('userToken');

    if (storedToken) {
      const decodedToken = jwtDecode(storedToken);

      // Check if the token is expired
      if (decodedToken.exp < Date.now() / 1000) {
        console.error('Token expired. Redirecting to homepage.');
        navigate('/'); // Redirect to homepage if token is expired
        return;
      }

      setToken(storedToken);

      if (storedData) {
        const parsedData = JSON.parse(storedData);

        // Check if bookingID exists before making the API call
        if (parsedData.bookingID) {
          fetchBookingData(parsedData.bookingID, storedToken);
        } else {
          console.error('Booking ID is missing in stored booking data');
        }
      }

      fetchPaymentMethods(storedToken);
    } else {
      console.error('No token found. Redirecting to homepage.');
      navigate('/'); // Redirect to homepage if no token is found
    }
  }, [navigate]);

  const fetchBookingData = async (bookingID, token) => {
    try {
      const response = await axios.get(`https://fboothapi.azurewebsites.net/api/booking/${bookingID}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setBookingData(response.data);
      if (response.data.boothID) {
        fetchBoothData(response.data.boothID, token);
      }
    } catch (error) {
      console.error('Error fetching booking data:', error);
      if (error.response?.status === 401) {
        console.error('Invalid token. Redirecting to homepage.');
        navigate('/');
      }
    }
  };

  const fetchBoothData = async (boothID, token) => {
    try {
      const response = await axios.get(`https://fboothapi.azurewebsites.net/api/booth/${boothID}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setBoothData(response.data);
    } catch (error) {
      console.error('Error fetching booth data:', error);
    }
  };

  const fetchPaymentMethods = async (token) => {
    try {
      const response = await axios.get('https://fboothapi.azurewebsites.net/api/payment-method/customer', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setPaymentMethods(response.data);
    } catch (error) {
      if (error.response) {
        console.error('Error response:', error.response);
        if (error.response.status === 401) {
          console.error('Invalid token. Redirecting to homepage.');
          navigate('/');
        } else {
          console.error('Error response status:', error.response.status);
        }
      } else if (error.request) {
        console.error('Error request:', error.request);
      } else {
        console.error('Error message:', error.message);
      }
      console.error('Error fetching payment methods:', error);
    }
  };

  const handlePaymentMethodChange = (id) => {
    setSelectedPaymentMethodID(id);
    sessionStorage.setItem('selectedPaymentMethodID', id);
  };

  const handleCheckout = async () => {
    if (!selectedPaymentMethodID) {
      alert('Please select a payment method.');
      return;
    }

    if (!bookingData) {
      console.error('No booking data found in session.');
      return;
    }

    const { bookingID } = bookingData;
    const transactionPayload = {
      description: 'Payment Successful',
      paymentMethodID: selectedPaymentMethodID,
      bookingID: bookingID,
      returnUrl: 'https://fbooth.vercel.app/paymentinform',
    };

    try {
      const response = await axios.post('https://fboothapi.azurewebsites.net/api/payment', transactionPayload, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      console.log('Transaction created successfully:', response.data);

      window.location.href = response.data.transactionUlr;
      sessionStorage.removeItem('bookingData');
      sessionStorage.removeItem('selectedPaymentMethodID');
      sessionStorage.setItem('transactionData', JSON.stringify(response.data));
      sessionStorage.setItem('successMessage', 'Booking completed successfully!');
    } catch (error) {
      if (error.response) {
        console.error('Error response data:', error.response.data);
      }
      console.error('Error creating transaction:', error.message);
    }
  };

  const formatTime = (dateTimeString) => {
    const date = new Date(dateTimeString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center py-10 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl w-full grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Session order</h2>
          <div className="space-y-6">
            {bookingData && (
              <div>
                <h3 className="text-lg font-semibold text-gray-800">{bookingData.customerBusinessID}</h3>
                <p className="text-sm text-gray-600">
                  <span className="font-semibold">Date:</span> {new Date(bookingData.startTime).toLocaleDateString()}
                </p>
                <p className="text-sm text-gray-600">
                  <span className="font-semibold">Time:</span> {formatTime(bookingData.startTime)} - {formatTime(bookingData.endTime)}
                </p>
                <p className="text-sm text-gray-600">
                  <span className="font-semibold">Fee Hire Booth:</span> {bookingData.hireBoothFee.toLocaleString()} VND
                </p>
                <h4 className="text-lg font-semibold text-gray-800 mt-4">Services Out of Booth:</h4>
                {bookingData.bookingServices && bookingData.bookingServices.length > 0 ? (
                  <ul className="list-disc pl-5">
                    {bookingData.bookingServices.map((service, index) => (
                      <li key={index} className="text-sm text-gray-600">
                        {service.serviceName} - Quantity: {service.quantity} - SubTotal: {service.subTotal.toLocaleString()} VND
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-gray-600">No services selected yet.</p>
                )}
              </div>
            )}
          </div>

          <h3 className="text-lg font-semibold text-gray-800 mt-6">Payment Methods</h3>
          <form className="mt-4 grid gap-4">
          {paymentMethods.map((method) => (
  <div key={method.paymentMethodID} className="relative">
    <input
      className="peer hidden"
      id={`radio_${method.paymentMethodID}`}
      type="radio"
      name="radio"
      onChange={() => handlePaymentMethodChange(method.paymentMethodID)}
    />
    <span
      className={`peer-checked:border-gray-700 absolute right-4 top-1/2 box-content block h-3 w-3 -translate-y-1/2 rounded-full border-8 border-gray-300 bg-white ${selectedPaymentMethodID === method.paymentMethodID ? 'peer-checked:border-gray-700' : ''}`}
    ></span>
    <label
      className={`bg-white peer-checked:border-2 peer-checked:border-gray-700 flex cursor-pointer select-none rounded-lg border border-gray-300 p-4 ${selectedPaymentMethodID === method.paymentMethodID ? 'border-gray-700' : ''}`}
      htmlFor={`radio_${method.paymentMethodID}`}
    >
      <img
        src={method.methodIconUrl}
        alt={method.paymentMethodName}
        className="h-8 w-8 mr-3" // Adjust the h and w values to fit your design
      />
      <div className="ml-5">
        <span className="mt-2 font-semibold">{method.paymentMethodName}</span>
      </div>
    </label>
  </div>
))}

          </form>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-md">
          {boothData && boothData?.boothPhotos && boothData?.boothPhotos.length > 0 ? (
            <img src={boothData?.boothPhotos[0]?.boothPhotoUrl} alt="Booth" className="rounded-lg mb-4 w-full h-[300px]" />
          ) : (
            <img src="https://via.placeholder.com/400x300" alt="Placeholder Booth" className="rounded-lg mb-4 w-full h-[300px]" />
          )}
          <h3 className="text-xl font-semibold text-gray-800 mb-4">{bookingData?.customerBusinessID}</h3>
          <p className="text-sm text-gray-600">
            <strong>Check-in:</strong> {new Date(bookingData?.startTime).toLocaleDateString()} at {formatTime(bookingData?.startTime)}
          </p>
          <p className="text-sm text-gray-600">
            <strong>Check-out:</strong> {new Date(bookingData?.endTime).toLocaleDateString()} at {formatTime(bookingData?.endTime)}
          </p>
          <div className="mt-4">
            <h4 className="text-lg font-semibold text-gray-800">Standard double room</h4>
            <p className="text-sm text-gray-600">Price per night: {bookingData?.hireBoothFee.toLocaleString()} VND</p>
          </div>
          <div className="mt-4 flex justify-between items-center border-t pt-4">
            <h4 className="text-xl font-semibold text-gray-800">TOTAL</h4>
            <p className="text-xl font-semibold text-gray-800">{bookingData?.totalPrice.toLocaleString()} VND</p>
          </div>
          <button
            onClick={handleCheckout}
            className="mt-6 w-full bg-blue-500 text-white py-3 rounded-lg shadow-lg hover:bg-blue-600"
          >
            Book now
          </button>
        </div>
      </div>
    </div>
  );
}
